import React from 'react'
import dayjs from 'dayjs'
import { DateRange, DayPicker } from 'react-day-picker'
import { ru } from 'react-day-picker/locale'

import { Button } from '../button'
import { Flex } from '../flex'
import { IconChevronLeft } from '../icon/icon-chevron-left'
import { Input } from '../input'
import styles from './calendar-range.module.scss'
import { DATE_FORMAT } from './constants'
import { useCalendarState } from './hooks/use-calendar-state'

interface CalendarDropdownProps {
    value?: DateRange
    hasMaxDate?: boolean
    ref: React.RefObject<HTMLDivElement>
    onChange: (value?: DateRange) => void
    onClose: () => void
}

const TODAY_DATE = new Date()

export const CalendarDropdown = React.forwardRef<HTMLDivElement, CalendarDropdownProps>(
    ({ value, onChange, hasMaxDate, onClose }, ref) => {
        const { selected, month, setMonth, setSelected, handleMonthChange } =
            useCalendarState(value)

        const handleApply = React.useCallback(() => {
            onChange(selected)
            onClose()
        }, [onChange, onClose, selected])

        const handleReset = React.useCallback(() => {
            setSelected(undefined)
            setMonth(TODAY_DATE)
            onChange()
        }, [onChange, setMonth, setSelected])

        return (
            <div ref={ref} className={styles['calendar__dropdown']}>
                <Flex direction='column' spacing='xl'>
                    <Flex grow={1} spacing='m' wrap='nowrap'>
                        <Input
                            label='От'
                            type='button'
                            value={selected?.from && dayjs(selected.from).format(DATE_FORMAT)}
                            onClick={() => setMonth(selected?.from || TODAY_DATE)}
                        />
                        <Input
                            label='До'
                            type='button'
                            value={selected?.to && dayjs(selected.to).format(DATE_FORMAT)}
                            onClick={() => setMonth(selected?.to || TODAY_DATE)}
                        />
                    </Flex>
                    <DayPicker
                        locale={ru}
                        mode='range'
                        month={dayjs(month).subtract(1, 'month').toDate()}
                        numberOfMonths={2}
                        selected={selected}
                        showOutsideDays={false}
                        components={{
                            Chevron: () => <IconChevronLeft size={20} />,
                        }}
                        disabled={
                            hasMaxDate
                                ? {
                                      after: TODAY_DATE,
                                  }
                                : undefined
                        }
                        onNextClick={() => handleMonthChange('next')}
                        onPrevClick={() => handleMonthChange('prev')}
                        onSelect={setSelected}
                    />
                    <Flex justify='flex-end' spacing='xs'>
                        <Button size='small' variant='secondary' onClick={handleReset}>
                            Сбросить
                        </Button>
                        <Button size='small' variant='primary' onClick={handleApply}>
                            Применить
                        </Button>
                    </Flex>
                </Flex>
            </div>
        )
    }
)

CalendarDropdown.displayName = 'CalendarDropdown'
